import { useState } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { NAVY, AMBER, BORDER_COLOR, bubbleStyle } from '../../styles/tokens';

const MONTH_NAMES = [
  'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
  'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre',
];
const DAY_NAMES = ['Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb', 'Dom'];

const TYPE_COLORS = {
  vacation: { bg: '#dbeafe', border: '#2563eb', text: '#1d4ed8' },
  sick:     { bg: '#fee2e2', border: '#dc2626', text: '#b91c1c' },
  personal: { bg: '#ede9fe', border: '#7c3aed', text: '#6d28d9' },
};

const PENDING_COLORS = { bg: '#fef9c3', border: '#eab308', text: '#a16207' };

export default function CalendarView({ events = [], month, year, onMonthChange }) {
  const [currentMonth, setCurrentMonth] = useState(month ?? new Date().getMonth());
  const [currentYear, setCurrentYear]   = useState(year ?? new Date().getFullYear());

  function navigate(dir) {
    let m = currentMonth + dir;
    let y = currentYear;
    if (m < 0)  { m = 11; y--; }
    if (m > 11) { m = 0;  y++; }
    setCurrentMonth(m);
    setCurrentYear(y);
    onMonthChange?.(m, y);
  }

  // Build calendar grid
  const firstDay = new Date(currentYear, currentMonth, 1);
  const lastDay  = new Date(currentYear, currentMonth + 1, 0);
  const startDow = (firstDay.getDay() + 6) % 7; // Mon=0
  const totalDays = lastDay.getDate();

  const cells = [];
  for (let i = 0; i < startDow; i++) cells.push(null);
  for (let d = 1; d <= totalDays; d++) cells.push(d);
  while (cells.length % 7 !== 0) cells.push(null);

  // Map events to days
  function getEventsForDay(day) {
    if (!day) return [];
    const dateStr = `${currentYear}-${String(currentMonth + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    return events.filter(e => {
      const start = e.start_date?.slice(0, 10) || e.start_date;
      const end   = e.end_date?.slice(0, 10) || e.end_date;
      return start <= dateStr && end >= dateStr;
    });
  }

  const today = new Date();
  const isToday = (day) =>
    day && currentMonth === today.getMonth() && currentYear === today.getFullYear() && day === today.getDate();

  return (
    <div style={{ ...bubbleStyle, padding: '16px 20px' }}>
      {/* Month navigation */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
        <button onClick={() => navigate(-1)} style={{
          background: 'none', border: `1px solid ${BORDER_COLOR}`, borderRadius: 6,
          padding: '4px 8px', cursor: 'pointer', color: NAVY,
        }}>
          <ChevronLeft size={16} />
        </button>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          {(currentMonth !== today.getMonth() || currentYear !== today.getFullYear()) && (
            <button
              onClick={() => { setCurrentMonth(today.getMonth()); setCurrentYear(today.getFullYear()); onMonthChange?.(today.getMonth(), today.getFullYear()); }}
              style={{
                padding: '4px 12px', fontSize: 11, fontWeight: 600,
                borderRadius: 6, cursor: 'pointer',
                border: `1px solid ${BORDER_COLOR}`,
                background: '#fff', color: NAVY,
                fontFamily: 'Inter, sans-serif',
              }}
            >
              Hoy
            </button>
          )}
          <span style={{ fontSize: 15, fontWeight: 700, color: NAVY }}>
            {MONTH_NAMES[currentMonth]} {currentYear}
          </span>
        </div>
        <button onClick={() => navigate(1)} style={{
          background: 'none', border: `1px solid ${BORDER_COLOR}`, borderRadius: 6,
          padding: '4px 8px', cursor: 'pointer', color: NAVY,
        }}>
          <ChevronRight size={16} />
        </button>
      </div>

      {/* Day headers */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 2, marginBottom: 4 }}>
        {DAY_NAMES.map(d => (
          <div key={d} style={{
            textAlign: 'center', fontSize: 11, fontWeight: 600,
            color: '#94a3b8', padding: '4px 0',
          }}>
            {d}
          </div>
        ))}
      </div>

      {/* Calendar cells */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 2 }}>
        {cells.map((day, i) => {
          const dayEvents = getEventsForDay(day);
          const isWeekend = i % 7 >= 5;
          return (
            <div key={i} style={{
              minHeight: 48, padding: '2px 4px',
              background: !day ? 'transparent' : isToday(day) ? '#fef3c7' : isWeekend ? '#f8fafc' : '#fff',
              borderRadius: 4,
              border: isToday(day) ? `1.5px solid ${AMBER}` : `1px solid ${day ? '#f1f5f9' : 'transparent'}`,
            }}>
              {day && (
                <>
                  <div style={{ fontSize: 11, fontWeight: isToday(day) ? 700 : 500, color: isWeekend ? '#94a3b8' : NAVY }}>
                    {day}
                  </div>
                  {dayEvents.slice(0, 2).map((e, j) => {
                    const isPending = e.status === 'pending';
                    const colors = isPending ? PENDING_COLORS : (TYPE_COLORS[e.type] || TYPE_COLORS.vacation);
                    return (
                      <div key={j} style={{
                        fontSize: 9, padding: '1px 3px', marginTop: 1,
                        background: colors.bg, color: colors.text,
                        borderRadius: 3, overflow: 'hidden',
                        whiteSpace: 'nowrap', textOverflow: 'ellipsis',
                        borderLeft: `2px solid ${colors.border}`,
                        borderStyle: isPending ? 'dashed' : 'solid',
                      }}>
                        {e.user_name?.split(' ')[0] || 'PTO'}{isPending ? ' ⏳' : ''}
                      </div>
                    );
                  })}
                  {dayEvents.length > 2 && (
                    <div style={{ fontSize: 9, color: '#94a3b8', marginTop: 1 }}>
                      +{dayEvents.length - 2} más
                    </div>
                  )}
                </>
              )}
            </div>
          );
        })}
      </div>

      {/* Legend */}
      <div style={{ display: 'flex', gap: 16, marginTop: 12, flexWrap: 'wrap' }}>
        {Object.entries(TYPE_COLORS).map(([key, colors]) => (
          <div key={key} style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 11 }}>
            <span style={{
              width: 10, height: 10, borderRadius: 2,
              background: colors.bg, border: `1.5px solid ${colors.border}`,
            }} />
            <span style={{ color: '#64748b' }}>
              {key === 'vacation' ? 'Vacaciones' : key === 'sick' ? 'Enfermedad' : 'Personal'}
            </span>
          </div>
        ))}
        <div style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 11 }}>
          <span style={{
            width: 10, height: 10, borderRadius: 2,
            background: PENDING_COLORS.bg, border: `1.5px dashed ${PENDING_COLORS.border}`,
          }} />
          <span style={{ color: '#64748b' }}>Pendiente</span>
        </div>
      </div>
    </div>
  );
}
